'use client';

import { useState } from 'react';
import z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import {
  ChevronLeft,
  Eye,
  EyeOff,
  LockKeyhole,
  Mail,
  MessageSquare,
} from 'lucide-react';
import {
  Modal,
  Input,
  Link,
  Button,
  Avatar,
  Tooltip,
  Separator,
} from '@mokoboko/components';

import { login } from '@/services/auth';
import { track, AnalyticsEvent } from '@/lib/analytics/tracker';
import { User, setSignedInUser } from '@/state/user';
import useTenant from '@/state/tenant';
import { useIsMobile } from '@/hooks/use-is-mobile';
import OtpInputGroup from '@/components/otp-input-group';
import { PatternModalHeader } from '@/components/dialogs/pattern-modal-header';
import { ResetPasswordDialog } from '@/components/dialogs/reset-password';
import { emailSchema, type EmailFormValues } from '@/lib/schemas';
import { useOtpFlow } from '@/hooks/use-otp-flow';
import { formatMaskedPhone, mergeClassNames } from '@/lib/utils';
import { GENERIC_ERROR_MESSAGE } from '@/lib/constants';
import { OTP_BLOCK_COPY } from '@/helpers/auth';

interface LoginDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (userData: User) => void;
  onCreateAccountLinkClick?: VoidFunction;
  defaultEmail?: string;
}

const passwordSchema = z.object({
  password: z
    .string()
    .min(1, { message: 'Please type in your account password' }),
});

type PasswordFormValues = { password: string };

type Step = 'email' | 'otp' | 'password';

export const LoginDialog = ({
  open,
  onConfirm,
  onClose,
  onCreateAccountLinkClick,
  defaultEmail,
}: LoginDialogProps) => {
  const { data: tenant } = useTenant();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>('email');
  const [showPassword, setShowPassword] = useState(false);
  const [shake, setShake] = useState(0);
  const [isPasswordProcessing, setIsPasswordProcessing] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [isResetOpen, setIsResetOpen] = useState(false);

  const isMobile = useIsMobile();
  const logoUrl = tenant?.logo || '/images/logo.png';

  const otpFlow = useOtpFlow({
    purpose: 'login',
    onVerifySuccess: (response) => {
      if (response?.user) {
        setSignedInUser(queryClient, response.user);
        onConfirm(response.user);
        resetAllForms();
        track({
          event: AnalyticsEvent.LOGIN_COMPLETED,
          properties: {
            method: 'otp',
            delivery_method: otpFlow.deliveryMethod === 'sms' ? 'sms' : 'email',
          },
        });
      }
    },
  });

  const emailForm = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: { email: defaultEmail ?? '' },
  });

  const passwordForm = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
  });

  const onEmailSubmit = async (data: EmailFormValues) => {
    try {
      const method = isMobile ? 'sms' : 'email';
      await otpFlow.submitEmail(data.email, method);
      setStep('otp');
      track({
        event: AnalyticsEvent.LOGIN_STARTED,
        properties: { method: method === 'sms' ? 'otp_sms' : 'otp_email' },
      });
    } catch {
      // Error already handled by hook (formError set)
    }
  };

  const onPasswordSubmit = async (data: PasswordFormValues) => {
    try {
      setIsPasswordProcessing(true);
      setPasswordError('');
      const response = await login({
        email: otpFlow.email,
        password: data.password,
        remember_me: true,
      });

      if (response?.user) {
        setSignedInUser(queryClient, response.user);
        onConfirm(response.user);
        resetAllForms();
        track({
          event: AnalyticsEvent.LOGIN_COMPLETED,
          properties: { method: 'password', delivery_method: null },
        });
      }
    } catch (error: any) {
      setPasswordError(
        error?.message || GENERIC_ERROR_MESSAGE
      );
      track({
        event: AnalyticsEvent.LOGIN_FAILED,
        properties: {
          method: 'password',
          failure_reason: error?.message || 'Unknown error',
        },
      });
    } finally {
      setIsPasswordProcessing(false);
    }
  };

  const resetAllForms = () => {
    emailForm.reset();
    passwordForm.reset();
    otpFlow.resetOtpState();
    setStep('email');
    setShowPassword(false);
    setPasswordError('');
    setIsResetOpen(false);
  };

  const handleClose = () => {
    resetAllForms();
    onClose();
  };

  const handleBack = () => {
    setStep('email');
    otpFlow.resetOtpState();
    passwordForm.reset();
  };

  const handleBackToOtp = () => {
    setStep('otp');
    setPasswordError('');
    otpFlow.otpForm.clearErrors();
    passwordForm.reset();
  };

  const AlternateIcon =
    otpFlow.deliveryMethod === 'email' ? MessageSquare : Mail;

  const emailReg = emailForm.register('email');

  // Swapped in rather than stacked, so the login dialog keeps its step and
  // email while the reset form shows, and "Found your password? Login" comes
  // back to the password step.
  if (isResetOpen) {
    return (
      <ResetPasswordDialog
        open={open}
        defaultEmail={otpFlow.email}
        onClose={handleClose}
        onLoginLinkClick={() => setIsResetOpen(false)}
      />
    );
  }

  return (
    <Modal.Root open={open} onOpenChange={(o) => !o && handleClose()}>
      <Modal.Content
        size="sm"
        hasCloseButton
        isPreventClose={otpFlow.isProcessing || isPasswordProcessing}
      >
        <PatternModalHeader className="sm:px-8 sm:py-8">
          {(step === 'otp' || step === 'password') && (
            <Button
              variant="secondary-gray"
              iconOnly
              size="md"
              type="button"
              onClick={step === 'password' ? handleBackToOtp : handleBack}
              className="absolute left-5 top-5 z-10"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
          )}

          {step === 'email' && (
            <Avatar
              src={logoUrl}
              name={tenant?.name || 'MB'}
              size="xl"
              className="relative bg-pine-600 text-white"
            />
          )}

          <Modal.Title
            className={mergeClassNames('relative text-xl text-gray-900', step === 'email' ? 'mt-5' : 'mt-7')}
          >
            {step === 'email' && 'Welcome back'}
            {step === 'otp' &&
              (otpFlow.deliveryMethod === 'sms'
                ? 'Check your texts'
                : 'Check your inbox')}
            {step === 'password' && 'Enter your password'}
          </Modal.Title>
          <Modal.Description className="relative mt-1 max-w-sm text-gray-700">
            {step === 'email' &&
              "Enter your email and we'll send you a quick login code. No password needed."}
            {step === 'otp' && (
              <>
                We sent a 6-digit code to{' '}
                <span className="font-semibold text-gray-900">
                  {otpFlow.deliveryMethod === 'sms'
                    ? formatMaskedPhone(otpFlow.maskedPhone) || 'your phone'
                    : otpFlow.email}
                </span>
                <br />
                Enter it below to log in.
              </>
            )}
            {step === 'password' && (
              <>
                Logging in as{' '}
                <span className="font-semibold text-gray-900">
                  {otpFlow.email}
                </span>
              </>
            )}
          </Modal.Description>
        </PatternModalHeader>

        <Modal.Body className="flex flex-col gap-6 overflow-visible p-5 pb-8 pt-0 sm:px-8 sm:pb-8">
          {step === 'email' && (
            <form
              // type="email" would otherwise let native validation block submit
              // before the zod resolver runs, replacing the styled field error
              // with the browser's own bubble.
              noValidate
              className="space-y-5"
              onSubmit={emailForm.handleSubmit(onEmailSubmit, () =>
                setShake((s) => s + 1)
              )}
            >
              <div className="space-y-4">
                <Input
                  label="Email"
                  type="email"
                  {...emailReg}
                  onChange={(e) => {
                    emailReg.onChange(e);
                    if (otpFlow.block) otpFlow.clearBlock();
                  }}
                  onBlur={(e) => {
                    const sanitized = e.target.value.trim().toLowerCase();
                    if (sanitized !== e.target.value) {
                      emailForm.setValue('email', sanitized, {
                        shouldValidate: true,
                      });
                    }
                    emailReg.onBlur(e);
                  }}
                  hasError={!!emailForm.formState.errors?.email}
                  errorMessage={emailForm.formState.errors?.email?.message}
                  shake={emailForm.formState.errors?.email ? shake : 0}
                  inputMode="email"
                  autoComplete="email"
                  placeholder="Enter your email"
                />

                <Button
                  fullWidth
                  isProcessing={otpFlow.isProcessing}
                  disabled={!!otpFlow.block}
                  type="submit"
                  size="lg"
                >
                  {otpFlow.isProcessing ? 'Sending code…' : 'Continue'}
                </Button>

                {otpFlow.formError && (
                  <p className="text-sm leading-5 text-error-500">
                    {otpFlow.formError}
                  </p>
                )}

                {otpFlow.block && otpFlow.blockCountdown && (
                  <p className="text-sm leading-5 text-error-500">
                    {OTP_BLOCK_COPY[otpFlow.block.reason]}{' '}
                    <span className="font-semibold">
                      {otpFlow.blockCountdown}
                    </span>
                    .
                  </p>
                )}
              </div>

              {onCreateAccountLinkClick && (
                <>
                  <Separator />

                  <div className="flex items-center justify-center gap-1">
                    <span className="text-base leading-6 text-gray-700 sm:text-sm sm:leading-5">
                      New here?
                    </span>
                    <Link
                      color="red"
                      size="sm"
                      className="text-base leading-6 sm:text-sm sm:leading-5"
                      onClick={onCreateAccountLinkClick}
                      role="button"
                      tabIndex={0}
                    >
                      Create account
                    </Link>
                  </div>
                </>
              )}
            </form>
          )}

          {step === 'otp' && (
            <form
              className="space-y-5"
              onSubmit={otpFlow.otpForm.handleSubmit(otpFlow.submitOtp)}
            >
              <div className="space-y-4">
                <OtpInputGroup
                  otp={otpFlow.otp}
                  hasError={!!otpFlow.otpForm.formState.errors?.otp}
                  errorMessage={
                    otpFlow.otpForm.formState.errors?.otp
                      ? `Please enter the 6-digit code we sent to your ${otpFlow.deliveryMethod === 'sms' ? 'phone' : 'email'}`
                      : undefined
                  }
                />
              </div>

              <div className="[&>span]:w-full">
                <Tooltip
                  content={
                    otpFlow.deliveryMethod === 'sms'
                      ? 'We sent a code to your phone. Enter it above to continue.'
                      : 'We sent a code to your email. Enter it above to continue.'
                  }
                  side="bottom"
                  isDisabled={otpFlow.canSubmitOtp || otpFlow.isProcessing}
                  className="max-w-60"
                >
                  <Button
                    fullWidth
                    isProcessing={otpFlow.isProcessing}
                    disabled={!otpFlow.canSubmitOtp}
                    type="submit"
                    size="lg"
                  >
                    {otpFlow.isProcessing ? 'Verifying…' : 'Log in'}
                  </Button>
                </Tooltip>
              </div>

              {otpFlow.formError && (
                <p className="text-sm leading-5 text-error-500">
                  {otpFlow.formError}
                </p>
              )}

              <div className="flex justify-center">
                <Link
                  size="md"
                  className="sm:!text-sm sm:!leading-5"
                  onClick={(e) => {
                    e.preventDefault();
                    otpFlow.handleResendOtp();
                  }}
                  isDisabled={otpFlow.resendCooldown > 0}
                  role="button"
                  tabIndex={0}
                >
                  {otpFlow.resendCooldown > 0
                    ? `Resend code in ${otpFlow.resendCooldown}s`
                    : 'Resend code'}
                </Link>
              </div>

              <Separator />

              <div className="flex flex-col gap-3">
                <p className="text-sm leading-5 text-gray-700">
                  Other ways to log in
                </p>

                {otpFlow.hasPhone && (
                  <Button
                    fullWidth
                    variant="secondary-gray"
                    size="lg"
                    type="button"
                    leadingIcon={<AlternateIcon className="h-5 w-5" />}
                    onClick={otpFlow.handleSwitchDeliveryMethod}
                    isProcessing={otpFlow.isSwitchingMethod}
                  >
                    {otpFlow.deliveryMethod === 'email'
                      ? 'Text me the code instead'
                      : 'Email me the code instead'}
                  </Button>
                )}

                <Button
                  fullWidth
                  variant="secondary-gray"
                  size="lg"
                  type="button"
                  leadingIcon={<LockKeyhole className="h-5 w-5" />}
                  onClick={() => {
                    setStep('password');
                    otpFlow.otpForm.clearErrors();
                  }}
                >
                  Use my password
                </Button>
              </div>
            </form>
          )}

          {step === 'password' && (
            <form
              className="space-y-5"
              onSubmit={passwordForm.handleSubmit(onPasswordSubmit, () =>
                setShake((s) => s + 1)
              )}
            >
              <div className="space-y-4">
                <Input
                  {...passwordForm.register('password')}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Your password"
                  hasError={!!passwordForm.formState.errors?.password}
                  errorMessage={
                    passwordForm.formState.errors?.password?.message
                  }
                  shake={passwordForm.formState.errors?.password ? shake : 0}
                  trailingIcon={showPassword ? EyeOff : Eye}
                  onTrailingIconClick={() => setShowPassword((v) => !v)}
                  trailingIconAriaLabel="Toggle password visibility"
                />
              </div>

              <Button
                fullWidth
                isProcessing={isPasswordProcessing}
                type="submit"
                size="lg"
              >
                {isPasswordProcessing ? 'Logging in…' : 'Log in'}
              </Button>

              {passwordError && (
                <p className="text-sm leading-5 text-error-500">
                  {passwordError}
                </p>
              )}

              <Separator />

              <div className="flex flex-col items-center gap-2">
                <div className="flex items-center justify-center gap-1">
                  <span className="text-base leading-6 text-gray-700 sm:text-sm sm:leading-5">
                    Forgot password?
                  </span>
                  <Link
                    color="red"
                    size="sm"
                    className="text-base leading-6 sm:text-sm sm:leading-5"
                    onClick={(e) => {
                      e.preventDefault();
                      handleBackToOtp();
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    Use login code
                  </Link>
                </div>
                <Link
                  color="red"
                  size="sm"
                  className="text-base leading-6 sm:text-sm sm:leading-5"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsResetOpen(true);
                  }}
                  role="button"
                  tabIndex={0}
                >
                  Email me a reset link
                </Link>
              </div>
            </form>
          )}
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
